import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ActionKey, type TournamentDto, type TournamentTemplateDto } from '@pokerroom/types';
import { api } from '../../lib/api';
import { useAuthStore } from '../../store/auth.store';

export default function TimedPricingControls({ tournament }: { tournament: TournamentDto }) {
  const { t } = useTranslation('tournament');
  const client = useQueryClient();
  const actions = useAuthStore((s) => s.allowedActions);
  const system = useAuthStore((s) => s.user?.isSystemUser);
  const can = (action: string) => !!system || !!actions?.includes(action);
  const canRepin = tournament.status === 'DRAFT' && can(ActionKey.TOURNAMENT_UPDATE);
  const canVerify =
    !!tournament.timedFeeSnapshot &&
    can(ActionKey.TOURNAMENT_MANUAL_FIX) &&
    !['COMPLETED', 'CANCELLED'].includes(tournament.status);
  const [templateId, setTemplateId] = useState('');
  const [phase, setPhase] = useState<'BEFORE_FIRST_BREAK' | 'AFTER_FIRST_BREAK'>(
    'BEFORE_FIRST_BREAK',
  );
  const [evidence, setEvidence] = useState('');
  const templates = useQuery({
    queryKey: ['templates'],
    queryFn: () => api.get<TournamentTemplateDto[]>('/tournament/templates'),
    enabled: canRepin,
  });
  const selected = templates.data?.find((row) => row.id === templateId);
  const refresh = () => void client.invalidateQueries({ queryKey: ['tournament', tournament.id] });
  const repin = useMutation({
    mutationFn: () =>
      api.post(`/tournament/${tournament.id}/timed-fee/repin`, {
        templateId,
        timedFeeVersionId: selected?.timedFeeVersion?.id,
        expectedFeeVersionId: tournament.timedFeeSnapshot?.id ?? '',
      }),
    onSuccess: refresh,
  });
  const verify = useMutation({
    mutationFn: () =>
      api.post(`/tournament/${tournament.id}/timed-first-break/verify`, {
        expectedRevision: tournament.timedFirstBreak?.revision ?? 0,
        phase,
        evidence,
      }),
    onSuccess: () => {
      setEvidence('');
      refresh();
    },
  });
  if (tournament.type !== 'TIMED') return null;
  return (
    <section className="mb-4 rounded-lg border border-base-300 p-4 space-y-3">
      <h2 className="font-semibold">{t('pricingControls.title')}</h2>
      <p>
        {t('pricingControls.phase')}:{' '}
        {t(`pricingControls.${tournament.timedFirstBreak?.phase ?? 'UNVERIFIED'}`)}
      </p>
      <p className="text-sm text-base-content/70">{t('pricingControls.history')}</p>
      {canRepin && (
        <div className="space-y-2">
          <label className="block">
            {t('pricingControls.template')}
            <select
              className="select select-bordered w-full"
              value={templateId}
              onChange={(e) => setTemplateId(e.target.value)}
            >
              <option value="">{t('pricingControls.select')}</option>
              {templates.data
                ?.filter((row) => row.type === 'TIMED' && row.timedFeeVersion)
                .map((row) => (
                  <option key={row.id} value={row.id}>
                    {row.name}
                  </option>
                ))}
            </select>
          </label>
          {selected?.timedFeeVersion && (
            <p>
              {t('pricingControls.principal')}: {selected.timedFeeVersion.table.principalAmount};{' '}
              {t('timedFees.original')}: {selected.timedFeeVersion.table.originalVenueFeeAmount}
            </p>
          )}
          <button
            className="btn btn-outline"
            disabled={!selected?.timedFeeVersion || repin.isPending}
            onClick={() => repin.mutate()}
          >
            {t('pricingControls.repin')}
          </button>
        </div>
      )}
      {canVerify && (
        <div className="space-y-2">
          <label className="block">
            {t('pricingControls.verifiedPhase')}
            <select
              className="select select-bordered w-full"
              value={phase}
              onChange={(e) => setPhase(e.target.value as typeof phase)}
            >
              <option value="BEFORE_FIRST_BREAK">{t('pricingControls.BEFORE_FIRST_BREAK')}</option>
              <option
                value="AFTER_FIRST_BREAK"
                disabled={!['IN_PROGRESS', 'PAUSED'].includes(tournament.status)}
              >
                {t('pricingControls.AFTER_FIRST_BREAK')}
              </option>
            </select>
          </label>
          <label className="block">
            {t('pricingControls.evidence')}
            <textarea
              className="textarea textarea-bordered w-full"
              maxLength={500}
              value={evidence}
              onChange={(e) => setEvidence(e.target.value)}
            />
          </label>
          <button
            className="btn btn-outline"
            disabled={!evidence.trim() || verify.isPending}
            onClick={() => verify.mutate()}
          >
            {t('pricingControls.verify')}
          </button>
        </div>
      )}
      {(repin.error || verify.error || templates.error) && (
        <p role="alert" className="text-error">
          {(repin.error ?? verify.error ?? templates.error)?.message}
        </p>
      )}
    </section>
  );
}
